import { useTranslation } from "react-i18next";
import Modal from "../base/Modal";
import { Button, Tabs, notifyError, notifySuccess } from "../ui";
import type { Quote } from "../../features/quotes/types";
import { useQuoteWizard, WIZARD_STEPS, type WizardStep } from "./useQuoteWizard";
import { ServiceStep } from "./steps/ServiceStep";
import { CalculationStep } from "./steps/CalculationStep";
import { ResultStep } from "./steps/ResultStep";
import type { QuoteSource } from "./quoteSource";

interface QuoteModalProps {
  source: QuoteSource;
  onClose: () => void;
  onSaved?: (quote: Quote) => void;
}

/**
 * Cotizador en tres pasos (servicio → cálculo → cotización). Se abre desde
 * rutas y viajes en Administración, desde Finanzas operativas y desde el CRM.
 */
export function QuoteModal({ source, onClose, onSaved }: QuoteModalProps) {
  const { t } = useTranslation("finanzasOperativas");
  const wizard = useQuoteWizard(source);
  const { step, setStep, saved } = wizard;
  const idx = WIZARD_STEPS.indexOf(step);

  const canCalculate =
    source.kind !== "blank" ||
    Boolean(wizard.service.route_id) ||
    (wizard.service.origin?.lat != null && wizard.service.destination?.lat != null);

  const tabs = WIZARD_STEPS.map((id, i) => ({
    id,
    label: t(`quotes.steps.${id}`),
    disabled: i > idx && !(i === 1 && wizard.server) && !(i === 2 && wizard.estimate),
  }));

  const handleNext = async () => {
    try {
      if (step === "service") await wizard.goToCalculation();
      else if (step === "calculation") setStep("result");
    } catch (err) {
      void notifyError(t("quotes.toasts.error"), (err as Error)?.message);
    }
  };

  const handleSave = async () => {
    try {
      const quote = await wizard.save();
      void notifySuccess(t("quotes.toasts.saved"), t("quotes.result.savedFolio", { folio: quote.folio }));
      onSaved?.(quote);
    } catch (err) {
      void notifyError(t("quotes.toasts.error"), (err as Error)?.message);
    }
  };

  const handlePdf = async () => {
    try {
      const meta = await wizard.generatePdf();
      if (meta?.url) {
        void notifySuccess(t("quotes.toasts.pdfReady"));
        window.open(meta.url, "_blank", "noopener");
      }
    } catch (err) {
      void notifyError(t("quotes.toasts.error"), (err as Error)?.message);
    }
  };

  const close = () => {
    document.body.style.overflow = "";
    onClose();
  };

  return (
    <Modal
      title={t("quotes.actions.quote")}
      onClose={close}
      width="w-full max-w-4xl"
      content={
        <div className="space-y-4">
          <Tabs variant="pills" items={tabs} activeId={step} onChange={(id) => setStep(id as WizardStep)} />

          {step === "service" && <ServiceStep wizard={wizard} source={source} />}
          {step === "calculation" && <CalculationStep wizard={wizard} />}
          {step === "result" && <ResultStep wizard={wizard} />}

          <div className="flex gap-3 border-t border-line pt-4">
            {step === "service" ? (
              <Button variant="outline" size="md" shape="pill" className="flex-1" onClick={close}>
                {t("common.cancel")}
              </Button>
            ) : (
              <Button variant="outline" size="md" shape="pill" className="flex-1" onClick={() => setStep(WIZARD_STEPS[idx - 1])} disabled={!!saved && step === "result"}>
                {t("common.back")}
              </Button>
            )}

            {step !== "result" && (
              <Button size="md" shape="pill" className="flex-1" loading={wizard.estimating} disabled={!canCalculate} onClick={() => void handleNext()}>
                {step === "service" ? t("quotes.actions.calculate") : t("common.next")}
              </Button>
            )}
            {step === "result" && !saved && (
              <Button size="md" shape="pill" className="flex-1" loading={wizard.saving} onClick={() => void handleSave()}>
                {t("quotes.result.saveDraft")}
              </Button>
            )}
            {step === "result" && saved && (
              <Button size="md" shape="pill" className="flex-1" loading={wizard.generatingPdf} icon={<i className="ri-file-pdf-2-line" />} onClick={() => void handlePdf()}>
                {saved.pdf?.url ? t("quotes.result.openPdf") : t("quotes.result.generatePdf")}
              </Button>
            )}
          </div>
        </div>
      }
    />
  );
}
